import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { LayoutHeader } from "@/Header";
import { SubscriptionPlan, ServiceType } from "@/business/subscription/types";
import { Tabs } from "@/Tabs";
import { useTab } from "^/common/useTab";
import {
  useBusinessMypageTicketList,
  useUpdateBusinessMypageTicketOrder,
} from "~/services/business/myPage";
import { DraggableList } from "@/business/subscription/DraggableList";

export const Route = createFileRoute(
  "/business/layout/user-subscription-order-edit"
)({
  component: SubscriptionOrderEditPage,
});

function SubscriptionOrderEditPage() {
  const navigate = useNavigate();
  const { tab, handleTab, tabList } = useTab([
    "유치원",
    "호텔",
    "데이케어",
    "미용실",
  ]);
  const [subscriptionPlans, setSubscriptionPlans] = useState<
    SubscriptionPlan[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);

  // API에서 실제 이용권 목록 가져오기
  const { data: ticketListData } = useBusinessMypageTicketList();
  const updateTicketOrderMutation = useUpdateBusinessMypageTicketOrder();

  // API 응답 타입 정의
  interface TicketItem {
    productTicketSeq: number;
    companySeq: number;
    cdTicketType: string;
    cdProductType: string;
    name: string;
    contents: string;
    cost: number;
    discount: number;
    amount: number;
    splitYn: string;
    splitCount: number;
    badge: string[];
    activeYn: string;
    dataOrder: number;
  }

  // API 응답을 SubscriptionPlan 형식으로 변환
  const convertTicketToPlan = (
    ticket: TicketItem,
    serviceType: ServiceType
  ): SubscriptionPlan => {
    // badge는 이미 배열로 받음
    const badges = ticket.badge || [];

    return {
      planSeq: ticket.productTicketSeq,
      planName: ticket.name,
      content: ticket.contents || "",
      price: ticket.cost,
      discountRate: ticket.discount || 0,
      discountedPrice: ticket.amount,
      badges: badges,
      activeYn: ticket.activeYn === "Y" ? "Y" : "N",
      displayOrder: ticket.dataOrder,
      serviceType: serviceType,
      isCountBased: ticket.splitYn === "Y",
      count: ticket.splitCount || undefined,
    };
  };

  // API 데이터를 SubscriptionPlan 배열로 변환
  const apiPlans = useMemo(() => {
    if (!ticketListData) return [];

    const plans: SubscriptionPlan[] = [];

    // 유치원
    if (ticketListData.kindergarten) {
      plans.push(
        ...ticketListData.kindergarten.map((ticket: TicketItem) =>
          convertTicketToPlan(ticket, "유치원")
        )
      );
    }

    // 호텔
    if (ticketListData.hotel) {
      plans.push(
        ...ticketListData.hotel.map((ticket: TicketItem) =>
          convertTicketToPlan(ticket, "호텔")
        )
      );
    }

    // 데이케어
    if (ticketListData.daycare) {
      plans.push(
        ...ticketListData.daycare.map((ticket: TicketItem) =>
          convertTicketToPlan(ticket, "데이케어")
        )
      );
    }

    // 미용실
    if (ticketListData.grooming) {
      plans.push(
        ...ticketListData.grooming.map((ticket: TicketItem) =>
          convertTicketToPlan(ticket, "미용실")
        )
      );
    }

    return plans;
  }, [ticketListData]);

  // API 데이터가 있으면 사용, 없으면 빈 배열
  useEffect(() => {
    if (apiPlans.length > 0) {
      setSubscriptionPlans(apiPlans);
    }
  }, [apiPlans]);

  // API 데이터 사용
  const currentPlansRaw = subscriptionPlans;

  // 선택된 탭에 해당하고 activeYn이 "Y"인 이용권만 필터링
  const filteredPlans = currentPlansRaw.filter(
    (plan) =>
      (plan.serviceType === tab || !plan.serviceType) && plan.activeYn === "Y"
  );

  // displayOrder에 따라 정렬 (없으면 기존 순서 유지)
  const currentPlans = [...filteredPlans].sort((a, b) => {
    const orderA = a.displayOrder ?? a.planSeq ?? 0;
    const orderB = b.displayOrder ?? b.planSeq ?? 0;
    return orderA - orderB;
  });

  // 드래그 종료 시 순서 변경 처리
  const handleReorder = (newPlans: SubscriptionPlan[]) => {
    // displayOrder 업데이트
    const updatedPlans = newPlans.map((p: SubscriptionPlan, index: number) => ({
      ...p,
      displayOrder: index + 1,
    }));

    // 상태 업데이트: 해당 탭의 이용권만 업데이트
    setSubscriptionPlans((prev) => {
      const otherPlans = prev.filter(
        (p) => p.serviceType !== tab || p.activeYn !== "Y"
      );
      return [...otherPlans, ...updatedPlans];
    });
  };

  const handleComplete = async () => {
    if (!ticketListData) return;

    setIsSaving(true);
    try {
      // 현재 화면에 표시된 활성 이용권들을 순서대로 dataOrder 부여
      const requestData = currentPlans.map((plan, index) => {
        // 원본 API 데이터에서 해당 이용권 찾기
        let originalTicket: TicketItem | undefined;
        const allTickets: TicketItem[] = [
          ...(ticketListData.kindergarten || []),
          ...(ticketListData.hotel || []),
          ...(ticketListData.daycare || []),
          ...(ticketListData.grooming || []),
        ];
        originalTicket = allTickets.find(
          (t) => t.productTicketSeq === plan.planSeq
        );

        if (!originalTicket) {
          throw new Error(`이용권을 찾을 수 없습니다: ${plan.planSeq}`);
        }

        return {
          productTicketSeq: originalTicket.productTicketSeq,
          companySeq: originalTicket.companySeq,
          cdTicketType: originalTicket.cdTicketType,
          cdProductType: originalTicket.cdProductType,
          name: originalTicket.name,
          contents: originalTicket.contents,
          cost: originalTicket.cost,
          discount: originalTicket.discount,
          amount: originalTicket.amount,
          splitYn: originalTicket.splitYn,
          splitCount: originalTicket.splitCount,
          badge: originalTicket.badge,
          activeYn: originalTicket.activeYn,
          dataOrder: index + 1, // 화면 순서대로 1부터 부여
        };
      });

      await updateTicketOrderMutation.mutateAsync(requestData);
      navigate({ to: "/business/layout/user-subscription-management" });
    } catch (error) {
      console.error("순서 저장 실패:", error);
      // TODO: 에러 메시지 표시
    } finally {
      setIsSaving(false);
    }
  };

  // 아이템 렌더링 함수
  const renderItem = (plan: SubscriptionPlan, _index: number) => {
    return (
      <div className="w-full h-14 flex items-center justify-between bg-white border-2 border-[#E9E9E9] rounded-xl px-4">
        <span className="text-base font-medium text-gray-800">
          {plan.planName}
        </span>
        <div
          className="flex flex-shrink-0 justify-center items-center w-8 h-8 text-gray-400 drag-handle cursor-grab active:cursor-grabbing touch-none"
          aria-label="드래그하여 순서 변경"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="9" cy="5" r="1.5" />
            <circle cx="9" cy="12" r="1.5" />
            <circle cx="9" cy="19" r="1.5" />
            <circle cx="15" cy="5" r="1.5" />
            <circle cx="15" cy="12" r="1.5" />
            <circle cx="15" cy="19" r="1.5" />
          </svg>
        </div>
      </div>
    );
  };

  return (
    <>
      <LayoutHeader
        title={"이용권 순서 조정"}
        hasBackBtn
        hasFootPrint={false}
      />
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[#F5F5F5]">
        <Tabs tabList={tabList} handleTab={handleTab} tab={tab}>
          <div className="flex flex-col flex-auto px-3 pt-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                {tab} 이용권 순서 조정
              </h2>
              <button
                onClick={handleComplete}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-lg transition-colors hover:bg-orange-500 disabled:opacity-50"
              >
                {isSaving ? "저장 중..." : "완료"}
              </button>
            </div>
            {currentPlans.length === 0 ? (
              <div className="flex flex-col justify-center items-center py-20">
                <p className="mb-4 text-gray-500">등록된 이용권이 없습니다.</p>
              </div>
            ) : (
              <DraggableList
                items={currentPlans.map((plan) => ({
                  ...plan,
                  id: plan.planSeq,
                }))}
                onReorder={(newItems) => {
                  handleReorder(newItems as SubscriptionPlan[]);
                }}
                renderItem={renderItem}
                enableDragHandle={true}
                longPressDuration={200}
                className="flex flex-col gap-3"
              />
            )}
          </div>
        </Tabs>
      </div>
    </>
  );
}
